import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ActionModal } from '../action-modal/action-modal';
import styles from './contest-terms-preview.module.css';

export interface ContestTermsPreviewProps {
    pdfUrl: string;
    linkClassName?: string;
    linkText?: string;
}

function getGooglePdfViewerUrl(pdfUrl: string) {
    return `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(pdfUrl)}`;
}

export function ContestTermsPreview({ pdfUrl, linkClassName, linkText = 'View Terms & Conditions' }: ContestTermsPreviewProps) {
    const [isOpen, setIsOpen] = useState(false);
    const [previewSrc, setPreviewSrc] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const objectUrlRef = useRef<string | null>(null);

    const revokeObjectUrl = useCallback(() => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    }, []);

    const openPreview = useCallback(() => {
        setIsOpen(true);
    }, []);

    const closePreview = useCallback(() => {
        setIsOpen(false);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            revokeObjectUrl();
            setPreviewSrc(null);
            setIsLoading(false);
            return undefined;
        }

        let cancelled = false;

        const loadPreview = async () => {
            setIsLoading(true);
            setPreviewSrc(null);
            revokeObjectUrl();

            try {
                const response = await fetch(pdfUrl, { credentials: 'include' });
                if (!response.ok) {
                    throw new Error('Failed to fetch terms PDF');
                }

                const blob = await response.blob();
                const pdfBlob = blob.type === 'application/pdf' ? blob : new Blob([await blob.arrayBuffer()], { type: 'application/pdf' });
                const objectUrl = URL.createObjectURL(pdfBlob);

                if (cancelled) {
                    URL.revokeObjectURL(objectUrl);
                    return;
                }

                objectUrlRef.current = objectUrl;
                setPreviewSrc(objectUrl);
            } catch {
                if (cancelled) {
                    return;
                }

                setPreviewSrc(getGooglePdfViewerUrl(pdfUrl));
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadPreview();

        return () => {
            cancelled = true;
        };
    }, [isOpen, pdfUrl, revokeObjectUrl]);

    useEffect(
        () => () => {
            revokeObjectUrl();
        },
        [revokeObjectUrl]
    );

    return (
        <>
            <button type="button" className={classNames(styles['contest-terms-preview__link'], linkClassName)} onClick={openPreview}>
                {linkText}
            </button>
            <ActionModal
                headerTitle="Terms & Conditions"
                variant="dark"
                isOpen={isOpen}
                onClose={closePreview}
                hideCancel
                shouldCloseOnOverlayClick
                contentClassname={styles['contest-terms-preview__modal-content']}
            >
                {isLoading && <p className={styles['contest-terms-preview__status']}>Loading terms and conditions...</p>}
                {!isLoading && previewSrc && (
                    <iframe title="Terms and Conditions" src={previewSrc} className={styles['contest-terms-preview__iframe']} />
                )}
            </ActionModal>
        </>
    );
}
